import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { RootState } from '../store'
import { updateUser } from '../store/actions/authActions'
import { authService } from '../services/authService'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card'
import {
    User,
    Building2,
    Phone,
    Mail,
    Lock,
    ShieldCheck,
    Copy,
    Check,
    Coins,
    CreditCard,
    ShieldAlert,
    Cpu,
    Layers,
    BarChart3,
    Sparkles,
    ArrowRight,
    Search as SearchIcon,
    FileText,
    CheckCircle,
    Download,
} from 'lucide-react'
import { IntegrationsPage } from './IntegrationsPage'
import paymentService, { computeGst } from '../services/paymentService'
import { PriceBreakdown } from '../components/ui/PriceBreakdown'
import { BillingDetailsForm, validateBilling, BillingFormValues, INDIAN_STATES } from '../components/ui/BillingDetailsForm'

interface ProfileInfoFormData {
    firstName: string
    lastName: string
    companyName: string
    contactNumber: string
}

interface PasswordFormData {
    currentPassword?: string
    newPassword?: string
    confirmNewPassword?: string
}

export const ProfilePage: React.FC = () => {
    const dispatch = useDispatch()
    const { user } = useSelector((state: RootState) => state.auth)
    const [isLoading, setIsLoading] = useState(false)
    const [tokenUsage, setTokenUsage] = useState<any>(null)
    const [billingData, setBillingData] = useState<any>(null)

    // Location state
    const [checkoutDetails, setCheckoutDetails] = useState<any>(null)

    // Billing form states
    const [starterBilling, setStarterBilling] = useState<BillingFormValues>({
        billingName: '',
        billingAddress: '',
        billingState: '',
        billingPan: '',
        billingGstin: '',
    })
    const [starterBillingErrors, setStarterBillingErrors] = useState<Partial<Record<keyof BillingFormValues, string>>>({})

    const [intelligenceBilling, setIntelligenceBilling] = useState<BillingFormValues>({
        billingName: '',
        billingAddress: '',
        billingState: '',
        billingPan: '',
        billingGstin: '',
    })
    const [intelligenceBillingErrors, setIntelligenceBillingErrors] = useState<Partial<Record<keyof BillingFormValues, string>>>({})

    // Billing modal states
    const [showBillingModal, setShowBillingModal] = useState(false)
    const [activePlanForModal, setActivePlanForModal] = useState<'starter' | 'intelligence' | null>(null)

    // Invoice and Receipt preview states
    const [showInvoicePreviewModal, setShowInvoicePreviewModal] = useState(false)
    const [showReceiptPreviewModal, setShowReceiptPreviewModal] = useState(false)
    const [selectedTopupId, setSelectedTopupId] = useState<number | null>(null)
    const [selectedTopupItem, setSelectedTopupItem] = useState<any>(null)

    // Invoice Customizer fields
    const [customBillingName, setCustomBillingName] = useState('')
    const [customBillingAddress, setCustomBillingAddress] = useState('')
    const [customBillingGstin, setCustomBillingGstin] = useState('')
    const [customBillingState, setCustomBillingState] = useState('')
    const [customBillingPan, setCustomBillingPan] = useState('')

    const invoiceIframeRef = React.useRef<HTMLIFrameElement>(null)
    const receiptIframeRef = React.useRef<HTMLIFrameElement>(null)
    const invoicePrintRef = React.useRef<HTMLDivElement>(null)
    const receiptPrintRef = React.useRef<HTMLDivElement>(null)

    // Per-card coupon & payment states
    const [growthCouponCode, setGrowthCouponCode] = useState('')
    const [growthAppliedCoupon, setGrowthAppliedCoupon] = useState<any>(null)
    const [growthCouponError, setGrowthCouponError] = useState('')
    const [growthIsValidating, setGrowthIsValidating] = useState(false)

    const [intelCouponCode, setIntelCouponCode] = useState('')
    const [intelAppliedCoupon, setIntelAppliedCoupon] = useState<any>(null)
    const [intelCouponError, setIntelCouponError] = useState('')
    const [intelIsValidating, setIntelIsValidating] = useState(false)

    const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false)
    const [isProcessingPayment, setIsProcessingPayment] = useState(false)

    // MFA state variables
    const [showMfaSetupModal, setShowMfaSetupModal] = useState(false)
    const [showMfaDisableModal, setShowMfaDisableModal] = useState(false)
    const [mfaSetupStep, setMfaSetupStep] = useState<1 | 2 | 3>(1)
    const [mfaSecret, setMfaSecret] = useState('')
    const [mfaQrCodeUrl, setMfaQrCodeUrl] = useState('')
    const [mfaBackupCodes, setMfaBackupCodes] = useState<string[]>([])
    const [mfaSetupCode, setMfaSetupCode] = useState('')
    const [mfaDisablePassword, setMfaDisablePassword] = useState('')
    const [mfaDisableCode, setMfaDisableCode] = useState('')
    const [isMfaSetupLoading, setIsMfaSetupLoading] = useState(false)
    const [isMfaDisableLoading, setIsMfaDisableLoading] = useState(false)
    const [isCopied, setIsCopied] = useState(false)

    const handleStartMfaSetup = async () => {
        setIsMfaSetupLoading(true)
        try {
            const res = await authService.setupMfa()
            if (res.success && res.data) {
                setMfaSecret(res.data.secret)
                setMfaQrCodeUrl(res.data.qrCodeDataUrl)
                setMfaBackupCodes(res.data.backupCodes)
                setMfaSetupStep(1)
                setMfaSetupCode('')
                setShowMfaSetupModal(true)
            } else {
                toast.error(res.message || 'Failed to initialize MFA setup.')
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to initialize MFA setup.')
        } finally {
            setIsMfaSetupLoading(false)
        }
    }

    const handleVerifyMfaSetup = async () => {
        if (!mfaSetupCode.trim()) {
            toast.error('Please enter the 6-digit code.')
            return
        }
        setIsMfaSetupLoading(true)
        try {
            const res = await authService.verifyMfaSetup(mfaSetupCode)
            if (res.success) {
                toast.success('MFA verified successfully!')
                // Update user in Redux store
                if (user) {
                    dispatch(updateUser({ ...user, mfaEnabled: true }))
                }
                setMfaSetupStep(3) // Go to backup codes display
            } else {
                toast.error(res.message || 'Invalid code. Please try again.')
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Verification failed. Please try again.')
        } finally {
            setIsMfaSetupLoading(false)
        }
    }

    const handleCopyBackupCodes = () => {
        navigator.clipboard.writeText(mfaBackupCodes.join('\n'))
        setIsCopied(true)
        toast.success('Backup codes copied to clipboard!')
        setTimeout(() => setIsCopied(false), 2000)
    }

    const handleDisableMfa = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!mfaDisablePassword || !mfaDisableCode) {
            toast.error('Password and verification code are required.')
            return
        }
        setIsMfaDisableLoading(true)
        try {
            const res = await authService.disableMfa(mfaDisablePassword, mfaDisableCode)
            if (res.success) {
                toast.success('MFA disabled successfully.')
                // Update user in Redux store
                if (user) {
                    dispatch(updateUser({ ...user, mfaEnabled: false }))
                }
                setShowMfaDisableModal(false)
                setMfaDisablePassword('')
                setMfaDisableCode('')
            } else {
                toast.error(res.message || 'Failed to disable MFA.')
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to disable MFA.')
        } finally {
            setIsMfaDisableLoading(false)
        }
    }

    const location = useLocation()
    const navigate = useNavigate()
    const queryParams = new URLSearchParams(location.search)
    const activeTab = queryParams.get('tab') || 'profile'

    const isGoogleUser = (user as any)?.authProvider === 'google' ||
                         (user as any)?.isGoogleUser === true ||
                         localStorage.getItem('authProvider') === 'google'

    useEffect(() => {
        if (isGoogleUser && activeTab === 'security') {
            navigate('/profile?tab=profile', { replace: true })
        }
    }, [isGoogleUser, activeTab, navigate])

    const fetchBillingData = async () => {
        try {
            const response = await paymentService.billingStatus()
            if (response.success && response.data) {
                setBillingData(response.data)
            }
        } catch (error) {
            console.error('Failed to fetch billing status:', error)
        }
    }

    const isIndia = checkoutDetails?.countryCode === 'IN' ||
        checkoutDetails?.location?.countryCode === 'IN' ||
        checkoutDetails?.location?.country?.toLowerCase() === 'india' ||
        false

    const getPlanPricing = (plan: 'starter' | 'intelligence') => {
        if (isIndia) {
            const basePrice = plan === 'starter' ? 2199 : 9999
            const coupon = plan === 'starter' ? growthAppliedCoupon : intelAppliedCoupon
            const discountPercent = coupon?.discountPercent ?? 0
            const billingVals = plan === 'starter' ? starterBilling : intelligenceBilling
            const gst = computeGst(basePrice, discountPercent, billingVals.billingState, billingVals.billingGstin)
            return {
                basePrice,
                discountPercent,
                discountAmount: gst.discountAmount,
                priceAfterDiscount: gst.priceAfterDiscount,
                taxAmount: gst.taxAmount,
                finalPrice: gst.finalInclTax,
                currency: 'INR',
                symbol: '₹',
            }
        } else {
            const basePrice = plan === 'starter' ? 20 : 49
            const coupon = plan === 'starter' ? growthAppliedCoupon : intelAppliedCoupon
            const discountPercent = coupon?.discountPercent ?? 0
            const discountAmount = basePrice * (discountPercent / 100)
            const finalPrice = basePrice - discountAmount
            return {
                basePrice,
                discountPercent,
                discountAmount,
                priceAfterDiscount: finalPrice,
                taxAmount: 0,
                finalPrice,
                currency: 'USD',
                symbol: '$',
            }
        }
    }

    useEffect(() => {
        const fetchTokenUsage = async () => {
            try {
                const response = await authService.getTokenUsage()
                if (response.success && response.data) {
                    setTokenUsage(response.data)
                }
            } catch (error) {
                console.error('Failed to fetch token usage:', error)
            }
        }
        fetchTokenUsage()
    }, [])

    useEffect(() => {
        if (activeTab === 'billing') {
            fetchBillingData()
            const fetchCheckoutDetails = async () => {
                try {
                    const res = await paymentService.getCheckoutDetails()
                    if (res.success && res.data) {
                        setCheckoutDetails(res.data)
                        const stateName = res.data.location?.region || 'Tamil Nadu'
                        setStarterBilling((prev) => ({ ...prev, billingState: stateName }))
                        setIntelligenceBilling((prev) => ({ ...prev, billingState: stateName }))
                    }
                } catch (err) {
                    console.error('Failed to fetch checkout location details:', err)
                }
            }
            fetchCheckoutDetails()

            if (!(window as any).Razorpay) {
                const script = document.createElement('script')
                script.src = 'https://checkout.razorpay.com/v1/checkout.js'
                script.async = true
                script.onload = () => setIsRazorpayLoaded(true)
                script.onerror = () => toast.error('Razorpay SDK failed to load.')
                document.body.appendChild(script)
                return () => { if (document.body.contains(script)) document.body.removeChild(script) }
            } else {
                setIsRazorpayLoaded(true)
            }
        }
    }, [activeTab])

    const handleTabChange = (tab: string) => {
        navigate(`/profile?tab=${tab}`)
    }

    const handleApplyCoupon = async (plan: 'growth' | 'intel') => {
        const code = plan === 'growth' ? growthCouponCode : intelCouponCode
        const setValidating = plan === 'growth' ? setGrowthIsValidating : setIntelIsValidating
        const setError = plan === 'growth' ? setGrowthCouponError : setIntelCouponError
        const setApplied = plan === 'growth' ? setGrowthAppliedCoupon : setIntelAppliedCoupon

        if (!code.trim()) { setError('Please enter a coupon code'); return }
        setValidating(true)
        setError('')
        try {
            const res = await paymentService.validateCoupon(code, plan === 'growth' ? 'starter' : 'intelligence')
            if (res.success && res.data) {
                setApplied(res.data)
                toast.success(`Coupon applied! ${res.data.discountPercent}% discount`)
            } else {
                setError(res.message || 'Invalid coupon code')
                setApplied(null)
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to validate coupon')
            setApplied(null)
        } finally {
            setValidating(false)
        }
    }

    const handleClearCoupon = (plan: 'growth' | 'intel') => {
        if (plan === 'growth') {
            setGrowthAppliedCoupon(null); setGrowthCouponCode(''); setGrowthCouponError('')
        } else {
            setIntelAppliedCoupon(null); setIntelCouponCode(''); setIntelCouponError('')
        }
    }

    const handleOpenInvoicePreview = (item: any) => {
        setSelectedTopupId(item.id)
        setSelectedTopupItem(item)
        const defaultName = (user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : (user as any)?.username || 'Customer'
        setCustomBillingName(defaultName)
        setCustomBillingAddress(item.billingAddress || '')
        setCustomBillingGstin(item.billingGstin || '')
        setCustomBillingState(item.billingState || 'Tamil Nadu')
        setCustomBillingPan(item.billingPan || '')
        setShowInvoicePreviewModal(true)
    }

    const handleOpenReceiptPreview = (item: any) => {
        setSelectedTopupId(item.id)
        setSelectedTopupItem(item)
        setShowReceiptPreviewModal(true)
    }

    const handleCheckout = async (type: 'starter' | 'intelligence', bypassModal = false) => {
        if (isIndia && !bypassModal) {
            setActivePlanForModal(type)
            setShowBillingModal(true)
            return
        }

        setIsProcessingPayment(true)
        const pricing = getPlanPricing(type)
        const appliedCoupon = type === 'starter' ? growthAppliedCoupon : intelAppliedCoupon
        const coupon = appliedCoupon?.couponName

        let billingPayload: any = undefined
        if (isIndia) {
            const billingVals = type === 'starter' ? starterBilling : intelligenceBilling
            const setErrors = type === 'starter' ? setStarterBillingErrors : setIntelligenceBillingErrors
            const errs = validateBilling(billingVals)
            if (Object.keys(errs).length > 0) {
                setErrors(errs)
                toast.error('Please complete billing details.')
                setIsProcessingPayment(false)
                return
            }
            billingPayload = {
                billingState: billingVals.billingState,
                billingPan: billingVals.billingPan,
                billingGstin: billingVals.billingGstin,
                billingAddress: billingVals.billingAddress,
            }
            setShowBillingModal(false)
        }
        try {
            if (!isRazorpayLoaded) {
                toast.error('Payment gateway is initializing. Please try again.')
                setIsProcessingPayment(false)
                return
            }

            // Create Razorpay Order on Backend
            const orderRes = await paymentService.createOrder(type, coupon, billingPayload)
            if (!orderRes.success) {
                toast.error(orderRes.message || 'Failed to initiate transaction.')
                setIsProcessingPayment(false)
                return
            }

            // If order bypass is requested (100% coupon)
            if (orderRes.bypass) {
                const result = await paymentService.upgradePlan(type, undefined, coupon, billingPayload)
                if (result.success) {
                    toast.success('🎉 Transaction successful! Resources provisioned.')
                    dispatch(updateUser({
                        ...(user as any),
                        planType: type,
                        tokensRemaining: result.data?.tokensRemaining ?? (user as any)?.tokensRemaining,
                        tokensTotal: result.data?.tokensTotal ?? (user as any)?.tokensTotal,
                        projectsAllowed: result.data?.projectsAllowed ?? (user as any)?.projectsAllowed,
                    } as any))
                    fetchBillingData()
                    handleClearCoupon(type === 'starter' ? 'growth' : 'intel')
                } else {
                    toast.error(result.message || 'Bypass transaction failed')
                }
                setIsProcessingPayment(false)
                return
            }

            const options = {
                key: orderRes.key_id || '',
                amount: orderRes.amount,
                currency: orderRes.currency || 'INR',
                name: 'DocSprintAI',
                description: `${type === 'starter' ? 'Growth' : 'Intelligence'} Plan`,
                image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciIHgxPSIwIiB5MT0iMCIgeDI9IjEiIHkyPSIxIj4KICAgICAgPHN0b3Agb2Zmc2V0PSIwIiBzdG9wLWNvbG9yPSIjZmI5MjNjIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iMSIgc3RvcC1jb2xvcj0iI2VhNTgwYyIvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIHJ4PSI0MCIgZmlsbD0idXJsKCNnKSIvPgogIDwhLS0gQ2VudGVyIFN0YXIgLS0+CiAgPHBhdGggZD0iTTEwMCA0MCBMMTE1IDg1IEwxNjAgMTAwIEwxMTUgMTE1IEwxMDAgMTYwIEw4NSAxMTUgTDQwIDEwMCBMODUgODUgWiIgZmlsbD0id2hpdGUiLz4KICA8IS0tIFNtYWxsIFN0YXIgLS0+CiAgPHBhdGggZD0iTTE2MCA0MCBMMTE1IDU1IEwxODAgNjAgTDE2NSA2NSBMMTYwIDgwIEwxNTUgNjUgTDE0MCA2MCBMMTU1IDU1IFoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPg==",
                order_id: orderRes.order_id,
                handler: async function (rzpResponse: any) {
                    try {
                        const result = await paymentService.verifyPayment(
                            rzpResponse.razorpay_payment_id,
                            rzpResponse.razorpay_order_id,
                            rzpResponse.razorpay_signature
                        )
                        if (result.success) {
                            toast.success('🎉 Payment successful! Resources provisioned.')
                            dispatch(updateUser({
                                ...(user as any),
                                planType: type,
                                tokensRemaining: result.data?.tokensRemaining ?? (user as any)?.tokensRemaining,
                                tokensTotal: result.data?.tokensTotal ?? (user as any)?.tokensTotal,
                                projectsAllowed: result.data?.projectsAllowed ?? (user as any)?.projectsAllowed,
                            } as any))
                            fetchBillingData()
                            handleClearCoupon(type === 'starter' ? 'growth' : 'intel')
                        } else {
                            toast.error(result.message || 'Failed to update account resources')
                        }
                    } catch {
                        toast.error('Payment succeeded but resource update failed. Contact support.')
                    } finally {
                        setIsProcessingPayment(false)
                    }
                },
                prefill: {
                    name: (user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : (user as any)?.username || 'User',
                    email: (user as any)?.email || '',
                    contact: (user as any)?.contactNumber || '',
                },
                theme: { color: type === 'intelligence' ? '#f97316' : '#10b981' },
                modal: { ondismiss: () => setIsProcessingPayment(false) },
            }

            const rzp = new (window as any).Razorpay(options)
            rzp.open()
            rzp.on('payment.failed', (resp: any) => {
                toast.error(resp.error.description || 'Payment failed')
                setIsProcessingPayment(false)
            })
        } catch (err: any) {
            console.error('Checkout error:', err)
            toast.error('Failed to initiate transaction.')
            setIsProcessingPayment(false)
        }
    }

    // Form for Profile Information
    const {
        register: registerProfile,
        handleSubmit: handleProfileSubmit,
        formState: { errors: profileErrors, isDirty: isProfileDirty },
    } = useForm<ProfileInfoFormData>({
        defaultValues: {
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            companyName: user?.companyName || '',
            contactNumber: user?.contactNumber || '',
        },
    })

    // Form for Password Updates
    const {
        register: registerPassword,
        handleSubmit: handlePasswordSubmit,
        watch: watchPassword,
        reset: resetPassword,
        formState: { errors: passwordErrors },
    } = useForm<PasswordFormData>({
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        },
    })

    const currentPassword = watchPassword('currentPassword')
    const newPassword = watchPassword('newPassword')

    const onProfileSubmit = async (data: ProfileInfoFormData) => {
        setIsLoading(true)
        try {
            const response = await authService.updateProfile(data)
            if (response.success) {
                toast.success('Profile details updated successfully!')
                setTimeout(() => window.location.reload(), 1000)
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update profile')
        } finally {
            setIsLoading(false)
        }
    }

    const onPasswordSubmit = async (data: PasswordFormData) => {
        if (data.currentPassword && data.newPassword && data.currentPassword === data.newPassword) {
            toast.error('New password must be different from your current password.')
            return
        }
        setIsLoading(true)
        try {
            const response = await authService.updateProfile({
                currentPassword: data.currentPassword,
                newPassword: data.newPassword,
                confirmNewPassword: data.confirmNewPassword
            })
            if (response.success) {
                toast.success('Password updated successfully!')
                resetPassword()
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update password')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div>
                <h1 className="text-3xl font-black tracking-tight text-white font-heading mb-1">Account Settings</h1>
            </div>

            {/* Navigation Tabs */}
            <div className="flex border-b border-white/10 gap-1 mb-6 overflow-x-auto scrollbar-none pb-px">
                {[
                    { id: 'profile', label: 'Profile Information', userOnly: false },
                    { id: 'security', label: 'Security & MFA', userOnly: false, manualOnly: true },
                    { id: 'billing', label: 'Billing & Tokens', userOnly: true },
                    { id: 'integrations', label: 'Integrations', userOnly: true }
                ]
                    .filter((tab) => !tab.userOnly || (user as any)?.role !== 'admin')
                    .filter((tab) => !tab.manualOnly || !isGoogleUser)
                    .map((tab) => {
                        const isActive = activeTab === tab.id
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className={`px-5 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all whitespace-nowrap ${isActive
                                    ? 'border-primary text-primary bg-primary/1'
                                    : 'border-transparent text-text-secondary hover:text-rose-400 hover:bg-red-500/10'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        )
                    })}
            </div>

            {/* Tab Contents */}
            <div className="min-h-[400px]">
                {/* ── Profile Tab ── */}
                {activeTab === 'profile' && (
                    <Card className="border-white/10 shadow-2xl shadow-black/20 bg-[#131B2C]/40 backdrop-blur-xl">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-7 border-b border-white/5">
                            <div>
                                <CardTitle className="text-white font-black uppercase tracking-tight flex items-center gap-2">
                                    <User className="w-5 h-5 text-primary" />
                                    Personal Information
                                </CardTitle>
                                <CardDescription className="text-text-secondary font-medium mt-1">
                                    Update your personal details and company info.
                                </CardDescription>
                            </div>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-xl text-[11px] font-black uppercase tracking-widest shadow-[0_0_15px_rgba(245,158,11,0.15)]">
                                <Coins className="w-3.5 h-3.5" />
                                Tokens: {tokenUsage ? (tokenUsage.tokensRemaining?.toLocaleString() ?? '0') : '...'}
                            </div>
                        </CardHeader>
                        <CardContent className="pt-8">
                            <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Input label="First name" icon={User} error={profileErrors.firstName?.message}
                                        {...registerProfile('firstName', { required: 'First name is required', minLength: { value: 2, message: 'Minimum 2 characters' } })} />
                                    <Input label="Last name" icon={User} error={profileErrors.lastName?.message}
                                        {...registerProfile('lastName', { required: 'Last name is required', minLength: { value: 2, message: 'Minimum 2 characters' } })} />
                                </div>
                                <div className="grid grid-cols-1 gap-6">
                                    <Input label="Email address" icon={Mail} value={user?.email || ''} disabled
                                        className="bg-[#0B0F19]/50 border-white/5 text-text-secondary cursor-not-allowed opacity-80" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Input label="Company" icon={Building2} error={profileErrors.companyName?.message}
                                        {...registerProfile('companyName', { required: 'Company name is required' })} />
                                    <Input label="Phone Number" icon={Phone} placeholder="+1 (555) 000-0000" error={profileErrors.contactNumber?.message}
                                        {...registerProfile('contactNumber', {
                                            required: 'Contact number is required',
                                            pattern: { value: /^\+?[1-9]\d{7,14}$/, message: 'Please enter a valid phone number' },
                                        })} />
                                </div>
                                <div className="flex items-center justify-end pt-6 border-t border-white/5">
                                    <Button type="submit" disabled={isLoading || !isProfileDirty} loading={isLoading}
                                        className="px-8 bg-primary hover:bg-orange-600 text-white shadow-[0_0_15px_rgba(255,107,0,0.3)]">
                                        Save Changes
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                )}

                {/* ── Security Tab ── */}
                {activeTab === 'security' && (
                    <div className="space-y-6">
                        <Card className="border-white/10 shadow-2xl shadow-black/20 bg-[#131B2C]/40 backdrop-blur-xl">
                            <CardHeader className="pb-7 border-b border-white/5">
                                <CardTitle className="text-white font-black uppercase tracking-tight flex items-center gap-2">
                                    <Lock className="w-5 h-5 text-primary" />
                                    Change Password
                                </CardTitle>
                                <CardDescription className="text-text-secondary font-medium mt-1">
                                    Ensure your account is using a long, random password to stay secure.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-8">
                                <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-6">
                                    <div className="grid grid-cols-1 gap-6">
                                        <Input label="Current Password" type="password" icon={Lock} placeholder="Enter current password to verify"
                                            error={passwordErrors.currentPassword?.message}
                                            {...registerPassword('currentPassword', { required: 'Current password is required to update password' })} />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <Input label="New Password" type="password" icon={ShieldCheck} placeholder="Min 8 chars, alpha-numeric"
                                            error={passwordErrors.newPassword?.message}
                                            {...registerPassword('newPassword', {
                                                required: 'New password is required',
                                                minLength: { value: 8, message: 'Minimum 8 characters' },
                                                pattern: { value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/, message: 'Uppercase, lowercase, digit, & symbol required' },
                                                validate: (val) => val !== currentPassword || 'New password must be different from your current password.',
                                            })} />
                                        <Input label="Confirm New Password" type="password" icon={ShieldCheck} placeholder="Repeat password"
                                            error={passwordErrors.confirmNewPassword?.message}
                                            {...registerPassword('confirmNewPassword', {
                                                required: 'Please confirm your new password',
                                                validate: (val) => val === newPassword || 'Passwords do not match',
                                            })} />
                                    </div>
                                    <div className="flex items-center justify-end pt-6 border-t border-white/5">
                                        <Button type="submit" disabled={isLoading} loading={isLoading}
                                            className="px-8 bg-primary hover:bg-orange-600 text-white shadow-[0_0_15px_rgba(255,107,0,0.3)]">
                                            Update Password
                                        </Button>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>

                        <Card className="border-white/10 shadow-2xl shadow-black/20 bg-[#131B2C]/40 backdrop-blur-xl">
                            <CardHeader className="pb-7 border-b border-white/5">
                                <CardTitle className="text-white font-black uppercase tracking-tight flex items-center justify-between gap-2">
                                    <span className="flex items-center gap-2">
                                        <ShieldAlert className="w-5 h-5 text-primary" />
                                        Two-Factor Authentication
                                    </span>
                                    {user?.mfaEnabled && (
                                        <span className="px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                                            Enabled
                                        </span>
                                    )}
                                </CardTitle>
                                <CardDescription className="text-text-secondary font-medium mt-1">
                                    Add an extra layer of security to your account.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-8 flex flex-col items-start gap-4">
                                <p className="text-sm text-text-secondary">
                                    Keep your account secure by enabling multi-factor authentication (MFA). You will need to enter a verification code from your authenticator app when logging in.
                                </p>
                                {user?.mfaEnabled ? (
                                    <Button type="button" onClick={() => setShowMfaDisableModal(true)}
                                        className="bg-red-600 hover:bg-red-700 text-white shadow-[0_0_15px_rgba(220,38,38,0.3)]">
                                        Disable MFA
                                    </Button>
                                ) : (
                                    <Button type="button" onClick={handleStartMfaSetup} disabled={isMfaSetupLoading} loading={isMfaSetupLoading}
                                        className="bg-primary hover:bg-orange-600 text-white shadow-[0_0_15px_rgba(255,107,0,0.3)]">
                                        Enable MFA
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Billing & Tokens Tab ── */}
                {activeTab === 'billing' && (
                    <div className="space-y-6">
                        {/* Plan Cards — Growth & Intelligence only (no top-up) */}

                        <div className="grid md:grid-cols-2 gap-8 items-stretch">
                            {/* Growth Plan */}
                            <div className="flex flex-col p-8 rounded-[2rem] bg-[#131B2C]/40 backdrop-blur-xl border border-emerald-500/10 shadow-2xl hover:border-emerald-500/30 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden group">
                                <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500/20 group-hover:bg-emerald-500 transition-colors"></div>
                                <div className="mb-6">
                                    <div className="inline-flex px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-[9px] font-black uppercase tracking-widest mb-4 border border-emerald-500/20">Advanced</div>
                                    <h3 className="text-2xl font-black text-white mb-1 uppercase tracking-tight">Growth</h3>
                                    <p className="text-text-secondary text-xs font-bold uppercase tracking-wider opacity-60">High-performance tier</p>
                                </div>
                                <div className="flex items-baseline mb-2">
                                    <span className="text-5xl font-black text-white font-heading">
                                        {getPlanPricing('starter').symbol}
                                        {isIndia
                                            ? getPlanPricing('starter').basePrice.toLocaleString('en-IN')
                                            : getPlanPricing('starter').basePrice.toFixed(0)
                                        }
                                    </span>
                                    <span className="text-emerald-500/60 ml-2 text-sm font-black uppercase tracking-widest">/ 1M Tokens</span>
                                </div>
                                {isIndia && (
                                    <div className="mb-4">
                                        <PriceBreakdown
                                            basePrice={2199}
                                            discountPercent={growthAppliedCoupon?.discountPercent ?? 0}
                                            billingState={starterBilling.billingState}
                                            billingGstin={starterBilling.billingGstin}
                                            accentColor="emerald"
                                        />
                                    </div>
                                )}
                                {growthAppliedCoupon && (
                                    <div className="flex items-center gap-2 mb-4 mt-1">
                                        <span className="line-through text-text-secondary text-sm">
                                            {getPlanPricing('starter').symbol}
                                            {getPlanPricing('starter').basePrice}
                                        </span>
                                        <span className="bg-emerald-500/15 text-emerald-400 text-[10px] font-black px-2 py-0.5 rounded-full border border-emerald-500/25">-{growthAppliedCoupon.discountPercent}% OFF</span>
                                    </div>
                                )}
                                <div className="bg-emerald-500/5 rounded-2xl p-4 mb-6 border border-emerald-500/10 space-y-2">
                                    <div className="flex justify-between text-[11px] font-black text-emerald-400/80 uppercase tracking-widest"><span>Compute</span><span className="text-white">1M Tokens</span></div>
                                    <div className="flex justify-between text-[11px] font-black text-emerald-400/80 uppercase tracking-widest"><span>Projects</span><span className="text-white">3 Projects</span></div>
                                </div>
                                <div className="space-y-4 mb-8 flex-1">
                                    {[
                                        { text: '1M Generative Tokens', icon: Cpu },
                                        { text: '3 Concurrent Projects', icon: Layers },
                                        { text: 'Full AI Intelligence Suite', icon: Sparkles },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center gap-3 text-sm text-text-secondary group-hover:text-white/90 transition-colors">
                                            <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 bg-emerald-500/10 text-emerald-400 border border-emerald-500/10">
                                                <item.icon size={16} />
                                            </div>
                                            <span className="font-bold text-xs uppercase tracking-wider">{item.text}</span>
                                        </div>
                                    ))}
                                </div>
                                {/* Growth — Coupon input */}
                                <div className="mb-4">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-emerald-400/70 mb-2 flex items-center gap-1">
                                        <span>🏷</span> Promo Code
                                    </p>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="ENTER CODE"
                                            value={growthCouponCode}
                                            onChange={(e) => { setGrowthCouponCode(e.target.value.toUpperCase()); setGrowthCouponError('') }}
                                            onKeyDown={(e) => e.key === 'Enter' && !growthAppliedCoupon && handleApplyCoupon('growth')}
                                            disabled={growthIsValidating || isProcessingPayment || !!growthAppliedCoupon}
                                            className="bg-black/40 border border-emerald-500/20 text-white rounded-xl px-3 py-2 text-[11px] font-bold uppercase tracking-wider focus:outline-none focus:border-emerald-500 flex-1 placeholder-white/20 transition-colors"
                                        />
                                        {growthAppliedCoupon ? (
                                            <button onClick={() => handleClearCoupon('growth')}
                                                className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-xl px-3 text-[10px] font-black uppercase tracking-wider transition-colors">
                                                ✕
                                            </button>
                                        ) : (
                                            <button onClick={() => handleApplyCoupon('growth')} disabled={growthIsValidating || isProcessingPayment}
                                                className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl px-4 text-[10px] font-black uppercase tracking-wider transition-colors disabled:opacity-50">
                                                {growthIsValidating ? '…' : 'Apply'}
                                            </button>
                                        )}
                                    </div>
                                    {growthCouponError && <p className="text-[9px] font-bold text-red-400 mt-1.5 uppercase tracking-wider">{growthCouponError}</p>}
                                    {growthAppliedCoupon && <p className="text-[9px] font-bold text-emerald-400 mt-1.5 uppercase tracking-wider">✓ {growthAppliedCoupon.discountPercent}% discount applied</p>}
                                </div>



                                <Button onClick={() => handleCheckout('starter')} disabled={isProcessingPayment} loading={isProcessingPayment}
                                    className="w-full h-14 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black uppercase tracking-[0.2em] hover:brightness-110 shadow-[0_0_20px_rgba(10,185,129,0.3)] transition-all flex items-center justify-center gap-2 border-none">
                                    {growthAppliedCoupon?.discountPercent === 100 ? <><span>Claim Free Growth</span><Sparkles size={16} /></> : <><span>Provision Growth</span><ArrowRight size={16} /></>}
                                </Button>
                            </div>

                            {/* Intelligence Plan */}
                            <div className="flex flex-col p-8 rounded-[2rem] bg-[#161F33] text-white shadow-[0_0_50px_rgba(0,0,0,0.5)] relative transition-all duration-500 hover:-translate-y-2 border border-primary/10 group ring-1 ring-primary/15">
                                <div className="absolute -inset-[1px] bg-gradient-to-r from-primary via-orange-500 to-amber-500 rounded-[2.1rem] -z-10 opacity-[0.12] group-hover:opacity-[0.22] blur-[2px] transition-opacity"></div>
                                <div className="absolute top-0 right-0 p-1">
                                    <div className="bg-gradient-to-r from-primary via-orange-500 to-amber-500 text-white text-[9px] font-black px-4 py-1.5 rounded-bl-2xl rounded-tr-[1.8rem] uppercase tracking-[0.3em] shadow-2xl flex items-center gap-2">
                                        <Sparkles size={10} fill="currentColor" /> ELITE
                                    </div>
                                </div>
                                <div className="mb-6 mt-4">
                                    <div className="inline-flex px-3 py-1 rounded-full bg-primary/20 text-primary text-[9px] font-black uppercase tracking-widest mb-4 border border-primary/30">Elite Integration</div>
                                    <h3 className="text-2xl font-black text-white mb-1 uppercase tracking-tight">Intelligence</h3>
                                    <p className="text-text-secondary text-xs font-bold uppercase tracking-wider opacity-60">Full stack AI enterprise</p>
                                </div>
                                <div className="flex items-baseline mb-2">
                                    <span className="text-5xl font-black text-white drop-shadow-[0_0_10px_rgba(255,107,0,0.2)]">
                                        {getPlanPricing('intelligence').symbol}
                                        {isIndia
                                            ? getPlanPricing('intelligence').basePrice.toLocaleString('en-IN')
                                            : getPlanPricing('intelligence').basePrice.toFixed(0)
                                        }
                                    </span>
                                    <span className="text-primary/60 ml-2 text-sm font-black uppercase tracking-widest">/ 5M Tokens</span>
                                </div>
                                {isIndia && (
                                    <div className="mb-4">
                                        <PriceBreakdown
                                            basePrice={9999}
                                            discountPercent={intelAppliedCoupon?.discountPercent ?? 0}
                                            billingState={intelligenceBilling.billingState}
                                            billingGstin={intelligenceBilling.billingGstin}
                                            accentColor="orange"
                                        />
                                    </div>
                                )}
                                {intelAppliedCoupon && (
                                    <div className="flex items-center gap-2 mb-4 mt-1">
                                        <span className="line-through text-text-secondary text-sm">
                                            {getPlanPricing('intelligence').symbol}
                                            {getPlanPricing('intelligence').basePrice}
                                        </span>
                                        <span className="bg-primary/15 text-primary text-[10px] font-black px-2 py-0.5 rounded-full border border-primary/25">-{intelAppliedCoupon.discountPercent}% OFF</span>
                                    </div>
                                )}

                                <div className="bg-primary/10 rounded-2xl p-4 mb-6 border border-primary/20 space-y-2 relative overflow-hidden">
                                    <div className="absolute inset-0 bg-primary/5 animate-pulse"></div>
                                    <div className="relative flex justify-between text-[11px] font-black text-primary uppercase tracking-widest"><span>Compute</span><span className="text-white">5M Tokens</span></div>
                                    <div className="relative flex justify-between text-[11px] font-black text-primary uppercase tracking-widest"><span>Projects</span><span className="text-white">UNLIMITED</span></div>
                                </div>
                                <div className="space-y-4 mb-6 flex-1">
                                    {[
                                        { text: '5M Generative Tokens', icon: Cpu },
                                        { text: 'Unlimited Projects', icon: Layers },
                                        { text: 'Elite Product Analysis', icon: BarChart3 },
                                        { text: 'Market Velocity Scanning', icon: SearchIcon },
                                        { text: 'Enterprise Deck Synthesis', icon: FileText },
                                        { text: 'Priority Support', icon: CheckCircle },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center gap-3 text-sm text-white group-hover:text-primary transition-colors">
                                            <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 bg-primary/10 text-primary border border-primary/20">
                                                <item.icon size={16} />
                                            </div>
                                            <span className="font-black text-[10px] uppercase tracking-widest">{item.text}</span>
                                        </div>
                                    ))}
                                </div>
                                {/* Intelligence — Coupon input */}
                                <div className="mb-4">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-primary/70 mb-2 flex items-center gap-1">
                                        <span>🏷</span> Promo Code
                                    </p>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="ENTER CODE"
                                            value={intelCouponCode}
                                            onChange={(e) => { setIntelCouponCode(e.target.value.toUpperCase()); setIntelCouponError('') }}
                                            onKeyDown={(e) => e.key === 'Enter' && !intelAppliedCoupon && handleApplyCoupon('intel')}
                                            disabled={intelIsValidating || isProcessingPayment || !!intelAppliedCoupon}
                                            className="bg-black/40 border border-primary/20 text-white rounded-xl px-3 py-2 text-[11px] font-bold uppercase tracking-wider focus:outline-none focus:border-primary flex-1 placeholder-white/20 transition-colors"
                                        />
                                        {intelAppliedCoupon ? (
                                            <button onClick={() => handleClearCoupon('intel')}
                                                className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-xl px-3 text-[10px] font-black uppercase tracking-wider transition-colors">
                                                ✕
                                            </button>
                                        ) : (
                                            <button onClick={() => handleApplyCoupon('intel')} disabled={intelIsValidating || isProcessingPayment}
                                                className="bg-gradient-to-r from-primary to-orange-500 hover:brightness-110 text-white rounded-xl px-4 text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-50">
                                                {intelIsValidating ? '…' : 'Apply'}
                                            </button>
                                        )}
                                    </div>
                                    {intelCouponError && <p className="text-[9px] font-bold text-red-400 mt-1.5 uppercase tracking-wider">{intelCouponError}</p>}
                                    {intelAppliedCoupon && <p className="text-[9px] font-bold text-primary mt-1.5 uppercase tracking-wider">✓ {intelAppliedCoupon.discountPercent}% discount applied</p>}
                                </div>



                                <Button onClick={() => handleCheckout('intelligence')} disabled={isProcessingPayment} loading={isProcessingPayment}
                                    className="w-full h-16 rounded-2xl bg-gradient-to-r from-primary via-orange-500 to-amber-500 text-white font-black uppercase tracking-[0.3em] hover:brightness-125 shadow-[0_0_30px_rgba(255,107,0,0.4)] transition-all flex items-center justify-center gap-2 border-none active:scale-[0.98]">
                                    {intelAppliedCoupon?.discountPercent === 100 ? <><span>Claim Free Elite</span><Sparkles size={18} /></> : <><span>Initialize Elite</span><ArrowRight size={18} /></>}
                                </Button>
                            </div>
                        </div>

                        {/* Payment History */}
                        <Card className="border-white/10 shadow-2xl shadow-black/20 bg-[#131B2C]/40 backdrop-blur-xl mt-8">
                            <CardHeader className="pb-5 border-b border-white/5">
                                <CardTitle className="text-white font-black uppercase tracking-tight flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-primary" />
                                    Payment History
                                </CardTitle>
                                <CardDescription className="text-text-secondary font-medium mt-1">
                                    Review your recent subscription upgrades and invoices.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="border-b border-white/10 text-xs font-black uppercase tracking-wider text-text-secondary">
                                                <th className="py-3 px-4">Date</th>
                                                <th className="py-3 px-4">Description</th>
                                                <th className="py-3 px-4">Amount</th>
                                                <th className="py-3 px-4">Method</th>
                                                <th className="py-3 px-4">Status</th>
                                                <th className="py-3 px-4 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/5 text-xs">
                                            {billingData?.topupHistory && billingData.topupHistory.length > 0 ? (
                                                billingData.topupHistory.map((item: any) => {
                                                    const dateStr = new Date(item.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                                                    return (
                                                        <tr key={item.id} className="text-white hover:bg-white/[0.02] transition-colors">
                                                            <td className="py-3.5 px-4 font-medium text-text-secondary">{dateStr}</td>
                                                            <td className="py-3.5 px-4 font-bold">
                                                                {item.tokensAdded > 0 ? `+${(item.tokensAdded / 1_000_000).toFixed(0)}M Tokens` : 'Upgrade Plan'}
                                                                {item.couponUsed && (
                                                                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[9px] font-black uppercase border border-emerald-500/20">{item.couponUsed}</span>
                                                                )}
                                                            </td>
                                                            <td className="py-3.5 px-4 font-bold text-white">
                                                                {item.currency === 'INR' ? '₹' : '$'}
                                                                {item.currency === 'INR'
                                                                    ? Number(item.totalAmount ?? item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                                    : Number(item.amount).toFixed(2)
                                                                }
                                                            </td>
                                                            <td className="py-3.5 px-4 font-medium text-text-secondary">{item.paymentMethod || 'Razorpay'}</td>
                                                            <td className="py-3.5 px-4">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${item.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' :
                                                                    item.status === 'failed' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'
                                                                    }`}>{item.status}</span>
                                                            </td>
                                                            <td className="py-3.5 px-4 text-right space-x-2 whitespace-nowrap">
                                                                {item.status === 'completed' && Number(item.amount) > 0 ? (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleOpenInvoicePreview(item)}
                                                                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 hover:border-primary/40 text-[10px] font-black uppercase tracking-wider transition-all"
                                                                        >
                                                                            <FileText className="w-3.5 h-3.5" />
                                                                            Invoice
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleOpenReceiptPreview(item)}
                                                                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 hover:border-emerald-500/40 text-[10px] font-black uppercase tracking-wider transition-all"
                                                                        >
                                                                            <CheckCircle className="w-3.5 h-3.5" />
                                                                            Receipt
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-[10px] font-bold text-text-secondary uppercase tracking-widest opacity-40">N/A</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={6} className="py-8 text-center text-text-secondary font-bold uppercase tracking-wider">
                                                        No payment transactions found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Integrations Tab ── */}
                {activeTab === 'integrations' && (
                    <div className="bg-transparent">
                        <IntegrationsPage />
                    </div>
                )}
            </div>

            {/* Billing Information Modal */}
            {showBillingModal && activePlanForModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
                    <div className="w-full max-w-md rounded-[2rem] bg-[#131B2C]/95 border border-white/10 p-6 shadow-2xl space-y-5 text-left relative">
                        <div>
                            <h3 className="text-xl font-black text-white uppercase tracking-tight">Billing Information</h3>
                            <p className="text-xs text-text-secondary font-medium">Please provide your billing details to generate the invoice.</p>
                        </div>

                        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                            <BillingDetailsForm
                                values={activePlanForModal === 'starter' ? starterBilling : intelligenceBilling}
                                onChange={activePlanForModal === 'starter' ? setStarterBilling : setIntelligenceBilling}
                                errors={activePlanForModal === 'starter' ? starterBillingErrors : intelligenceBillingErrors}
                                accentColor={activePlanForModal === 'starter' ? 'emerald' : 'orange'}
                            />
                        </div>

                        <div className="flex items-center gap-3 pt-3 border-t border-white/5">
                            <button
                                onClick={() => setShowBillingModal(false)}
                                className="flex-1 h-12 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-xs uppercase tracking-wider transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    // Submit and proceed
                                    handleCheckout(activePlanForModal, true)
                                }}
                                className={`flex-1 h-12 rounded-xl text-white font-black text-xs uppercase tracking-widest transition-all ${activePlanForModal === 'starter'
                                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:brightness-110 shadow-[0_0_15px_rgba(10,185,129,0.3)]'
                                    : 'bg-gradient-to-r from-primary to-orange-500 hover:brightness-110 shadow-[0_0_15px_rgba(255,107,0,0.3)]'
                                    }`}
                            >
                                {getPlanPricing(activePlanForModal).finalPrice === 0
                                    ? (activePlanForModal === 'starter' ? 'Claim Free Growth' : 'Claim Free Elite')
                                    : 'Proceed to Payment'
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Invoice Preview Modal ── */}
            {showInvoicePreviewModal && selectedTopupId && selectedTopupItem && (() => {
                const item = selectedTopupItem
                const taxableVal = Number(item.amount) || 0
                const taxAmt = Number(item.taxAmount) || 0
                const cgst = Number(item.cgst) || 0
                const sgst = Number(item.sgst) || 0
                const igst = Number(item.igst) || 0
                const total = Math.round((taxableVal + taxAmt) * 100) / 100
                const isTN = (customBillingState || '').toLowerCase() === 'tamil nadu'
                const dateStr = new Date(item.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                const invNum = `DSAI/26-27/INV-${item.id}`
                const fmt = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                const Rs = '\u20B9'
                let planName = 'DocSprintAI Plan Upgrade'
                if (item.tokensAdded === 1_000_000 || item.tokensAdded === 10_000_000) planName = 'Growth Plan Subscription Upgrade'
                else if (item.tokensAdded === 5_000_000) planName = 'Intelligence Plan Subscription Upgrade'
                const apiHost = (() => { const r = import.meta.env.VITE_API_URL || 'http://localhost:3333'; return r.endsWith('/api') ? r.slice(0, -4) : r })()
                const pdfUrl = `${apiHost}/api/payment/invoices/${selectedTopupId}/pdf?token=${localStorage.getItem('token')}&name=${encodeURIComponent(customBillingName)}&address=${encodeURIComponent(customBillingAddress)}&gstin=${encodeURIComponent(customBillingGstin)}&state=${encodeURIComponent(customBillingState)}&pan=${encodeURIComponent(customBillingPan)}`

                const printInvoice = () => {
                    const w = window.open('', '_blank')
                    if (!w) return
                    w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Invoice ${invNum}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Inter',sans-serif;background:#f3f4f6;padding:32px;color:#1f2937;font-size:13px}
    .card{background:#fff;border-radius:16px;box-shadow:0 4px 24px rgba(0,0,0,.10);max-width:780px;margin:0 auto;padding:40px 48px}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px}
    .company-name{font-size:22px;font-weight:900;color:#2563eb;letter-spacing:-.5px}
    .meta{font-size:10px;color:#6b7280;margin-top:2px;line-height:1.6}
    .tax-label{font-size:15px;font-weight:900;color:#2563eb;text-transform:uppercase;letter-spacing:2px;text-align:right}
    .inv-meta{font-size:10px;color:#6b7280;text-align:right;line-height:1.8;margin-top:4px}
    hr{border:none;border-top:1px solid #e5e7eb;margin:20px 0}
    .section-row{display:flex;justify-content:space-between;gap:24px;margin-bottom:24px}
    .section-title{font-size:11px;font-weight:700;color:#111827;margin-bottom:6px;text-transform:uppercase;letter-spacing:.5px}
    .billed-name{font-size:13px;font-weight:700;color:#1f2937}
    .billed-meta{font-size:11px;color:#6b7280;line-height:1.7}
    .pay-detail{font-size:11px;color:#4b5563;line-height:1.7;text-align:right}
    .pay-id{font-size:11px;font-weight:600;color:#1f2937;text-align:right}
    .paid-badge{font-size:11px;font-weight:700;color:#059669;text-align:right;margin-top:4px}
    table{width:100%;border-collapse:collapse;margin-bottom:20px}
    thead tr{background:#f9fafb}
    th{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#6b7280;padding:10px 12px;text-align:left}
    th:not(:first-child){text-align:center}
    th:last-child,th:nth-child(4){text-align:right}
    tbody tr{border-top:1px solid #f3f4f6}
    td{padding:12px 12px;font-size:12px;color:#374151}
    td:nth-child(2),td:nth-child(3){text-align:center;color:#6b7280}
    td:nth-child(4),td:nth-child(5){text-align:right;color:#1f2937}
    .totals{display:flex;justify-content:flex-end;margin-bottom:24px}
    .totals-box{width:260px}
    .totals-row{display:flex;justify-content:space-between;font-size:13px;color:#4b5563;padding:3px 0}
    .totals-divider{border:none;border-top:1px solid #d1d5db;margin:8px 0}
    .totals-final{display:flex;justify-content:space-between;font-size:15px;font-weight:700;color:#2563eb;padding:4px 0}
    .bank-box{border:1px solid #bfdbfe;background:#eff6ff;border-radius:10px;padding:12px 16px;font-size:10px;color:#374151;margin-bottom:16px;line-height:1.7}
    .bank-box strong{color:#111827;display:block;margin-bottom:4px;font-size:11px}
    .footer-note{border:1px solid #e5e7eb;border-radius:8px;padding:12px;text-align:center;font-size:10px;color:#9ca3af;line-height:1.7}
    @media print{body{background:#fff;padding:0} .card{box-shadow:none;border-radius:0;padding:32px}}
  </style>
</head>
<body>
  <div class="card">
    <div class="header">
      <div>
        <div class="company-name">BAIGEN TECHNOLABS</div>
        <div class="meta">BAIGEN TECHNOLABS (OPC) PRIVATE LIMITED</div>
        <div class="meta">2nd Floor 73C, 74C Workzen Business Centre,</div>
        <div class="meta">Old MTH Road, S Park Street, Ambattur, Chennai, Tamil Nadu 600053</div>
        <div class="meta">Phone: +91 7358015717 | +91 9342069623</div>
        <div class="meta">GSTIN: 33AAOCB3105G1Z2 | PAN: AAOCB3105G</div>
      </div>
      <div>
        <div class="tax-label">Tax Invoice</div>
        <div class="inv-meta">Invoice No: ${invNum}</div>
        <div class="inv-meta">Date: ${dateStr}</div>
        <div class="inv-meta">Place of Supply: ${customBillingState}</div>
        <div class="inv-meta">Supply Type: ${customBillingGstin ? 'B2B' : 'B2C'}</div>
        <div class="inv-meta">HSN/SAC: 998311</div>
      </div>
    </div>
    <hr/>
    <div class="section-row">
      <div>
        <div class="section-title">Billed To</div>
        <div class="billed-name">${customBillingName || '—'}</div>
        ${customBillingAddress ? `<div class="billed-meta">${customBillingAddress}</div>` : ''}
        ${customBillingPan ? `<div class="billed-meta">PAN: ${customBillingPan}</div>` : ''}
        ${customBillingGstin ? `<div class="billed-meta">GSTIN: ${customBillingGstin}</div>` : ''}
      </div>
      <div style="text-align:right">
        <div class="section-title" style="text-align:right">Payment Details</div>
        <div class="pay-detail">Payment Method: ${item.paymentMethod || 'Razorpay'}</div>
        <div class="pay-detail">Razorpay Payment ID:</div>
        <div class="pay-id">${item.razorpayPaymentId || 'Bypass/Coupon'}</div>
        <div class="paid-badge">Status: PAID</div>
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Service Description</th>
          <th>HSN/SAC</th>
          <th>Qty</th>
          <th>Taxable Val</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>${planName}</td>
          <td>998311</td>
          <td>1</td>
          <td>${Rs}${fmt(taxableVal)}</td>
          <td>${Rs}${fmt(taxableVal)}</td>
        </tr>
      </tbody>
    </table>
    <div class="totals">
      <div class="totals-box">
        <div class="totals-row"><span>Taxable Subtotal</span><span>${Rs}${fmt(taxableVal)}</span></div>
        ${isTN
                            ? `<div class="totals-row"><span>CGST @ 9%</span><span>${Rs}${fmt(cgst)}</span></div>
               <div class="totals-row"><span>SGST @ 9%</span><span>${Rs}${fmt(sgst)}</span></div>`
                            : `<div class="totals-row"><span>IGST @ 18%</span><span>${Rs}${fmt(igst)}</span></div>`
                        }
        <hr class="totals-divider"/>
        <div class="totals-final"><span>Invoice Total</span><span>${Rs}${fmt(total)}</span></div>
      </div>
    </div>
    <div class="bank-box">
      <strong>Bank Account Details (Paid via Razorpay)</strong>
      Account Name: BAIGEN TECHNOLABS (OPC) PRIVATE LIMITED<br/>
      Payment Gateway: Razorpay | Payment ID: ${item.razorpayPaymentId || 'Bypass/Coupon'}
    </div>
    <div class="footer-note">
      This is a system-generated invoice and does not require a physical signature.<br/>
      Thank you for your business!
    </div>
  </div>
  <script>window.onload=function(){window.print()}<\/script>
</body>
</html>`)
                    w.document.close()
                }

                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="w-full max-w-5xl max-h-[92vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
                                <h2 className="text-base font-bold text-gray-900">Invoice Preview</h2>
                                <button onClick={() => setShowInvoicePreviewModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors text-xl leading-none">×</button>
                            </div>

                            {/* Body — two-column */}
                            <div className="flex flex-1 min-h-0 overflow-hidden">

                                {/* ── Left: Billing Info (READ ONLY) ── */}
                                <div className="w-64 shrink-0 border-r border-gray-200 bg-gray-50 p-5 overflow-y-auto flex flex-col gap-4">
                                    {/* Section header */}
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Billing Info</span>
                                        <span className="text-[10px] font-semibold text-gray-400 border border-gray-300 rounded px-1.5 py-0.5 uppercase tracking-wider">Read Only</span>
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <label className="text-[11px] font-semibold text-gray-600">Customer / Company Name</label>
                                        <div className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white select-none cursor-default min-h-[38px]">
                                            {customBillingName || <span className="text-gray-300">—</span>}
                                        </div>
                                    </div>

                                    {customBillingAddress && (
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[11px] font-semibold text-gray-600">Billing Address</label>
                                            <div className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white select-none cursor-default whitespace-pre-wrap">
                                                {customBillingAddress}
                                            </div>
                                        </div>
                                    )}

                                    {customBillingGstin && (
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[11px] font-semibold text-gray-600">Customer GSTIN</label>
                                            <div className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white select-none cursor-default font-mono tracking-wide">
                                                {customBillingGstin}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex flex-col gap-1">
                                        <label className="text-[11px] font-semibold text-gray-600">Place of Supply (State)</label>
                                        <div className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white select-none cursor-default">
                                            {customBillingState || 'Tamil Nadu'}
                                        </div>
                                    </div>

                                    {customBillingPan && (
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[11px] font-semibold text-gray-600">PAN Number</label>
                                            <div className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white select-none cursor-default font-mono tracking-wide">
                                                {customBillingPan}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ── Right: Invoice Preview ── */}
                                <div className="flex-1 overflow-y-auto bg-gray-100 p-5">
                                    <div ref={invoicePrintRef} className="bg-white rounded-xl shadow-sm p-7 text-gray-800 text-sm" style={{ fontFamily: "'Segoe UI','Inter',Arial,sans-serif" }}>

                                        {/* Seller + Tax Invoice header */}
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <p className="text-[17px] font-black text-blue-700 tracking-tight">BAIGEN TECHNOLABS</p>
                                                <p className="text-[10px] text-blue-500 font-semibold mt-0.5">BAIGEN TECHNOLABS (OPC) PRIVATE LIMITED</p>
                                                <p className="text-[10px] text-gray-500 mt-1.5 leading-[1.65]">
                                                    2nd Floor 73C, 74C Workzen Business Centre,<br />
                                                    Old MTH Road, S Park Street, Chennai, Tamil Nadu 600053<br />
                                                    Phone: +91 7358015717 | +91 9342069623<br />
                                                    GSTIN: 33AAOCB3105G1Z2 | PAN: AAOCB3105G
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[15px] font-black text-blue-600 uppercase tracking-[2px]">TAX INVOICE</p>
                                                <div className="mt-2 space-y-[3px] text-[10px] text-gray-500">
                                                    <p><span className="font-medium text-gray-700">Invoice No:</span> {invNum}</p>
                                                    <p><span className="font-medium text-gray-700">Date:</span> {dateStr}</p>
                                                    <p><span className="font-medium text-gray-700">Place of Supply:</span> {customBillingState}</p>
                                                    <p><span className="font-medium text-gray-700">Supply Type:</span> {customBillingGstin ? 'B2B' : 'B2C'}</p>
                                                    <p><span className="font-medium text-gray-700">HSN/SAC:</span> 998311</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border-b-2 border-blue-600 my-3" />

                                        {/* Billed To + Payment Details */}
                                        <div className="flex justify-between gap-6 mb-5">
                                            <div>
                                                <p className="text-[11px] font-bold text-gray-700 mb-1">Billed To:</p>
                                                <p className="text-sm font-bold text-gray-900">{customBillingName || '—'}</p>
                                                {customBillingAddress && <p className="text-[11px] text-gray-500 mt-0.5">{customBillingAddress}</p>}
                                                {customBillingPan && <p className="text-[11px] text-gray-600 mt-0.5">PAN: <span className="font-semibold">{customBillingPan}</span></p>}
                                                {customBillingGstin && <p className="text-[11px] text-gray-600">GSTIN: <span className="font-semibold">{customBillingGstin}</span></p>}
                                            </div>
                                            <div className="text-right text-[11px] text-gray-600 space-y-0.5">
                                                <p>Payment Method: <span className="font-medium text-gray-800">{item.paymentMethod === 'bypass' ? 'Coupon' : (item.paymentMethod || 'Razorpay')}</span></p>
                                                <p>Razorpay Payment ID:</p>
                                                <p className="font-semibold text-gray-800">{item.razorpayPaymentId || 'Bypass/Coupon'}</p>
                                                <p className="text-green-600 font-bold mt-1">Status: <span>PAID</span></p>
                                            </div>
                                        </div>

                                        {/* Service Table */}
                                        <table className="w-full text-xs border-collapse mb-4">
                                            <thead>
                                                <tr className="bg-gray-100 text-gray-600 uppercase text-[10px] tracking-wider">
                                                    <th className="text-left py-2 px-3 font-bold border-b border-gray-200">Service Description</th>
                                                    <th className="text-center py-2 px-3 font-bold border-b border-gray-200">HSN/SAC</th>
                                                    <th className="text-center py-2 px-3 font-bold border-b border-gray-200">QTY</th>
                                                    <th className="text-right py-2 px-3 font-bold border-b border-gray-200">Taxable Val</th>
                                                    <th className="text-right py-2 px-3 font-bold border-b border-gray-200">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td className="py-3 px-3 text-gray-800 font-semibold">{planName}</td>
                                                    <td className="py-3 px-3 text-center text-gray-500">998311</td>
                                                    <td className="py-3 px-3 text-center text-gray-500">1</td>
                                                    <td className="py-3 px-3 text-right text-gray-700">{Rs}{fmt(taxableVal)}</td>
                                                    <td className="py-3 px-3 text-right font-semibold text-gray-900">{Rs}{fmt(taxableVal)}</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        {/* Totals */}
                                        <div className="flex justify-end mb-5">
                                            <div className="w-60 space-y-1 text-sm">
                                                <div className="flex justify-between text-gray-600 text-xs">
                                                    <span>Taxable Subtotal:</span>
                                                    <span>{Rs}{fmt(taxableVal)}</span>
                                                </div>
                                                {isTN ? (<>
                                                    <div className="flex justify-between text-gray-600 text-xs">
                                                        <span>CGST @ 9%:</span>
                                                        <span>{Rs}{fmt(cgst)}</span>
                                                    </div>
                                                    <div className="flex justify-between text-gray-600 text-xs">
                                                        <span>SGST @ 9%:</span>
                                                        <span>{Rs}{fmt(sgst)}</span>
                                                    </div>
                                                </>) : (
                                                    <div className="flex justify-between text-gray-600 text-xs">
                                                        <span>IGST @ 18%:</span>
                                                        <span>{Rs}{fmt(igst)}</span>
                                                    </div>
                                                )}
                                                <div className="border-t border-gray-300 pt-1.5 flex justify-between font-bold text-blue-600 text-sm">
                                                    <span>Invoice Total:</span>
                                                    <span>{Rs}{fmt(total)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bank details */}
                                        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 text-[10px] text-gray-700 mb-3">
                                            <p className="font-bold text-blue-700 mb-1">Bank Account Details (Paid via Razorpay)</p>
                                            <p>Account Name: BAIGEN TECHNOLABS (OPC) PRIVATE LIMITED</p>
                                            <p>Payment Gateway: Razorpay | Payment ID: {item.razorpayPaymentId || 'Bypass/Coupon'}</p>
                                        </div>

                                        <div className="text-center text-[10px] text-gray-400 leading-5 pt-1">
                                            <p>This is a system-generated invoice and does not require a physical signature.</p>
                                            <p>Thank you for your business!</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-white shrink-0">
                                <button onClick={() => setShowInvoicePreviewModal(false)}
                                    className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button onClick={() => window.open(`${pdfUrl}&download=true`, '_blank')}
                                    className="px-5 py-2.5 rounded-xl bg-gray-900 hover:bg-black text-white text-sm font-bold flex items-center gap-2 transition-colors">
                                    <Download className="w-4 h-4" /> Download PDF
                                </button>
                                <button onClick={printInvoice}
                                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold flex items-center gap-2 transition-colors">
                                    <FileText className="w-4 h-4" /> Print / Save PDF
                                </button>
                            </div>
                        </div>
                    </div>
                )
            })()}

            {/* ── Receipt Preview Modal ── */}
            {showReceiptPreviewModal && selectedTopupId && selectedTopupItem && (() => {
                const item = selectedTopupItem
                const baseAmt = Number(item.amount) || 0
                const taxAmt = Number(item.taxAmount) || 0
                const total = item.currency === 'INR' ? Math.round((baseAmt + taxAmt) * 100) / 100 : baseAmt
                const fmt = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                const Rs = '\u20B9'  // Rupee symbol via explicit Unicode escape
                const dateStr = new Date(item.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                const recNum = `REC-${item.id}`
                const userName = (user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : (user as any)?.username || 'Customer'
                let planName = 'DocSprintAI Plan Upgrade'
                if (item.tokensAdded === 1_000_000 || item.tokensAdded === 10_000_000) planName = 'Growth Upgrade Plan'
                else if (item.tokensAdded === 5_000_000) planName = 'Intelligence Upgrade Plan'
                const apiHost = (() => { const r = import.meta.env.VITE_API_URL || 'http://localhost:3333'; return r.endsWith('/api') ? r.slice(0, -4) : r })()
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="w-full max-w-xl max-h-[92vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
                                <h2 className="text-lg font-bold text-gray-900">Payment Receipt Preview</h2>
                                <button onClick={() => setShowReceiptPreviewModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors text-lg">×</button>
                            </div>

                            {/* Receipt card */}
                            <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
                                <div ref={receiptPrintRef} className="bg-white rounded-xl shadow p-8 max-w-md mx-auto text-gray-800" style={{ fontFamily: "'Segoe UI', 'Arial Unicode MS', 'Noto Sans', Arial, sans-serif" }}>
                                    {/* Logo */}
                                    <div className="text-center mb-2">
                                        <p className="text-2xl font-black text-blue-600">DocSprintAI</p>
                                        <p className="text-xs font-semibold text-gray-500 tracking-widest uppercase mt-0.5">Payment Receipt</p>
                                    </div>

                                    {/* Amount big display */}
                                    <div className="bg-gray-50 rounded-xl py-6 text-center my-4">
                                        <p className="text-4xl font-black text-gray-900">
                                            {item.currency === 'INR' ? Rs : '$'}{fmt(total)}
                                        </p>
                                        <span className="inline-block mt-3 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
                                            Payment Successful
                                        </span>
                                    </div>

                                    <hr className="border-dashed border-gray-200 my-4" />

                                    {/* Details table */}
                                    <div className="space-y-3 text-sm">
                                        {[
                                            { label: 'Receipt Number:', value: recNum },
                                            { label: 'Payment Date:', value: dateStr },
                                            { label: 'Payment Method:', value: item.paymentMethod || 'Razorpay' },
                                            { label: 'Razorpay Payment ID:', value: item.razorpayPaymentId || 'Bypass/Coupon' },
                                            { label: 'Received From:', value: userName },
                                            { label: 'Paid For:', value: planName },
                                        ].map(({ label, value }) => (
                                            <div key={label} className="flex justify-between gap-4">
                                                <span className="text-gray-500 shrink-0">{label}</span>
                                                <span className="font-bold text-gray-800 text-right">{value}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <hr className="border-dashed border-gray-200 my-4" />

                                    <p className="text-center font-bold text-gray-700 text-sm">Thank you for your trust in DocSprintAI!</p>
                                    <p className="text-center text-[10px] text-gray-400 mt-2">If you have any questions about this receipt, please contact sales@baigenlabs.com.</p>
                                    <p className="text-center text-[10px] text-gray-400">BAIGEN TECHNOLABS (OPC) PRIVATE LIMITED, Chennai, India.</p>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-white shrink-0">
                                <button onClick={() => setShowReceiptPreviewModal(false)}
                                    className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button onClick={() => window.open(`${apiHost}/api/payment/receipts/${selectedTopupId}/pdf?token=${localStorage.getItem('token')}&download=true`, '_blank')}
                                    className="px-5 py-2.5 rounded-xl bg-gray-900 hover:bg-black text-white text-sm font-bold flex items-center gap-2 transition-colors">
                                    <Download className="w-4 h-4" /> Download PDF
                                </button>
                                <button onClick={() => {
                                    const w = window.open('', '_blank')
                                    if (w && receiptPrintRef.current) {
                                        w.document.write(`<html><head><title>Receipt</title><style>body{font-family:sans-serif;margin:24px;max-width:480px;margin:24px auto}</style></head><body>${receiptPrintRef.current.innerHTML}</body></html>`)
                                        w.document.close(); w.print()
                                    }
                                }}
                                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold flex items-center gap-2 transition-colors">
                                    <FileText className="w-4 h-4" /> Print / Save PDF
                                </button>
                            </div>
                        </div>
                    </div>
                )
            })()}

            {/* ── MFA Setup Modal ── */}
            {showMfaSetupModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="w-full max-w-md rounded-2xl bg-[#0F172A] border border-white/10 shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 text-white">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 shrink-0">
                            <h2 className="text-lg font-black uppercase tracking-wider text-white">Setup Two-Factor (MFA)</h2>
                            {mfaSetupStep !== 3 && (
                                <button onClick={() => setShowMfaSetupModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/5 text-text-secondary transition-colors text-lg">×</button>
                            )}
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-6">
                            {/* Step Indicator */}
                            <div className="flex items-center justify-between px-2 text-xs font-black uppercase tracking-widest text-text-secondary">
                                <span className={mfaSetupStep === 1 ? 'text-primary' : ''}>1. Scan QR</span>
                                <span className="h-[1px] flex-1 bg-white/5 mx-3"></span>
                                <span className={mfaSetupStep === 2 ? 'text-primary' : ''}>2. Verify Code</span>
                                <span className="h-[1px] flex-1 bg-white/5 mx-3"></span>
                                <span className={mfaSetupStep === 3 ? 'text-primary' : ''}>3. Backup Codes</span>
                            </div>

                            {mfaSetupStep === 1 && (
                                <div className="space-y-4 text-center">
                                    <p className="text-sm text-text-secondary text-left font-medium">
                                        Scan the QR code below using your authenticator app (Google Authenticator, Authy, etc.).
                                    </p>
                                    <div className="bg-white p-4 rounded-xl inline-block shadow-lg">
                                        <img src={mfaQrCodeUrl} alt="MFA QR Code" className="w-48 h-48 mx-auto" />
                                    </div>
                                    <div className="text-left bg-white/5 rounded-xl p-4 border border-white/5 space-y-1">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Or Enter Manually:</span>
                                        <p className="font-mono text-sm break-all font-bold tracking-wider text-white select-all">{mfaSecret}</p>
                                    </div>
                                    <Button type="button" onClick={() => setMfaSetupStep(2)}
                                        className="w-full bg-primary hover:bg-orange-600 text-white font-black uppercase tracking-wider py-3.5 shadow-[0_0_15px_rgba(255,107,0,0.2)]">
                                        I've Scanned It
                                    </Button>
                                </div>
                            )}

                            {mfaSetupStep === 2 && (
                                <div className="space-y-4">
                                    <p className="text-sm text-text-secondary font-medium">
                                        Enter the 6-digit confirmation code generated by your authenticator app to complete setup.
                                    </p>
                                    <div className="space-y-2">
                                        <Input
                                            label="Verification Code"
                                            type="text"
                                            placeholder="e.g. 123456"
                                            value={mfaSetupCode}
                                            onChange={(e) => setMfaSetupCode(e.target.value)}
                                            maxLength={6}
                                            className="text-center font-mono text-xl tracking-[0.5em] font-bold"
                                        />
                                    </div>
                                    <div className="flex gap-3 pt-2">
                                        <Button type="button" onClick={() => setMfaSetupStep(1)} disabled={isMfaSetupLoading}
                                            className="flex-1 bg-white/5 hover:bg-white/10 text-white font-bold py-3">
                                            Back
                                        </Button>
                                        <Button type="button" onClick={handleVerifyMfaSetup} disabled={isMfaSetupLoading} loading={isMfaSetupLoading}
                                            className="flex-1 bg-primary hover:bg-orange-600 text-white font-black uppercase tracking-wider py-3 shadow-[0_0_15px_rgba(255,107,0,0.2)]">
                                            Verify
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {mfaSetupStep === 3 && (
                                <div className="space-y-4">
                                    <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 p-4 rounded-xl text-xs font-semibold leading-relaxed">
                                        MFA setup has been verified and enabled successfully! Save these backup codes in a safe place.
                                    </div>
                                    <p className="text-xs text-text-secondary font-medium">
                                        Each backup code is single-use and can be used to log in if you lose access to your authenticator device.
                                    </p>
                                    <div className="grid grid-cols-2 gap-2 p-4 bg-white/5 rounded-xl border border-white/5 font-mono text-center font-bold tracking-wider text-sm select-all">
                                        {mfaBackupCodes.map((code) => (
                                            <div key={code} className="py-1.5 text-white/90">{code}</div>
                                        ))}
                                    </div>
                                    <div className="flex gap-3 pt-2">
                                        <button onClick={handleCopyBackupCodes}
                                            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-white/10 hover:bg-white/5 text-white text-xs font-black uppercase tracking-wider transition-all">
                                            {isCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-primary" />}
                                            {isCopied ? 'Copied!' : 'Copy All'}
                                        </button>
                                        <Button type="button" onClick={() => setShowMfaSetupModal(false)}
                                            className="flex-1 bg-primary hover:bg-orange-600 text-white font-black uppercase tracking-wider py-3 shadow-[0_0_15px_rgba(255,107,0,0.2)]">
                                            Close
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── MFA Disable Modal ── */}
            {showMfaDisableModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
                    <form onSubmit={handleDisableMfa} className="w-full max-w-md rounded-2xl bg-[#0F172A] border border-white/10 shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 text-white">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 shrink-0">
                            <h2 className="text-lg font-black uppercase tracking-wider text-white">Disable Two-Factor (MFA)</h2>
                            <button type="button" onClick={() => setShowMfaDisableModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/5 text-text-secondary transition-colors text-lg">×</button>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-text-secondary font-medium leading-relaxed">
                                To disable multi-factor authentication, please enter your current account password and the 6-digit verification code from your authenticator app.
                            </p>
                            <div className="space-y-4">
                                <Input
                                    label="Current Password"
                                    type="password"
                                    placeholder="Enter your password to verify"
                                    value={mfaDisablePassword}
                                    onChange={(e) => setMfaDisablePassword(e.target.value)}
                                    required
                                />
                                <Input
                                    label="Verification Code"
                                    type="text"
                                    placeholder="e.g. 123456"
                                    value={mfaDisableCode}
                                    onChange={(e) => setMfaDisableCode(e.target.value)}
                                    maxLength={6}
                                    required
                                    className="text-center font-mono text-lg font-bold"
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/5 bg-[#0b1120] shrink-0">
                            <button type="button" onClick={() => setShowMfaDisableModal(false)}
                                className="px-5 py-2.5 rounded-xl border border-white/10 text-white text-sm font-semibold hover:bg-white/5 transition-colors">
                                Cancel
                            </button>
                            <Button type="submit" disabled={isMfaDisableLoading} loading={isMfaDisableLoading}
                                className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-black uppercase tracking-wider shadow-[0_0_15px_rgba(220,38,38,0.2)] animate-pulse">
                                Disable MFA
                            </Button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    )
}
